import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import authService from '../services/authService';

// Types
interface User {
  _id: string;
  name: string;
  email: string;
  profilePicture: string;
  phoneNumber?: string;
  newUser?: boolean;
  // Keep these fields for compatibility with existing components
  profileImage?: string;
  subscriptionPlan?: string;
  subscriptionExpiry?: string;
  remainingMinutes?: number;
  activePlan?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  signup: (name: string, email: string, password: string) => Promise<void>;
  refreshUserData: () => Promise<void>;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  login: async () => {},
  logout: async () => {},
  signup: async () => {},
  refreshUserData: async () => {},
});

// Provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Format user data to ensure compatibility with existing components
  const formatUserData = (userData: any): User => {
    // Log original user data to debug profile picture issues
    
    const formattedUser = {
      _id: userData._id || userData.id,
      name: userData.name,
      email: userData.email,
      profilePicture: userData.profilePicture,
      phoneNumber: userData.phoneNumber,
      newUser: userData.newUser,
      // Map to fields expected by existing components
      profileImage: userData.profilePicture,
      subscriptionPlan: userData.subscriptionPlan || 'Free Plan',
      subscriptionExpiry: userData.subscriptionExpiry || 'Not subscribed',
      remainingMinutes: userData.remainingMinutes || 20,
      activePlan: userData.activePlan || 'free'
    };
    
    
    return formattedUser;
  };

  // Load user profile from API
  const loadUserProfile = async () => {
    try {
      if (authService.isAuthenticated()) {
        const userData = await authService.getCurrentUser(true);
        if (userData) {
          setUser(formatUserData(userData));
        }
      }
    } catch (error) {
      console.error('Failed to load user profile:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Check for existing session on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      setIsLoading(true);
      try {
        // Check if user is authenticated with the token
        if (authService.isAuthenticated()) {
          await loadUserProfile();
        } else {
          setUser(null);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setUser(null);
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Refresh user data from API
  const refreshUserData = async (): Promise<void> => {
    if (authService.isAuthenticated()) {
      await loadUserProfile();
    }
  };

  // Login function
  const login = async (email: string, password: string): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await authService.login({ email, password });
      
      // Check if response has user data directly or in data property
      const userData = response?.user || response.user;
      
      if (userData) {
        setUser(formatUserData(userData));
      } else {
        // If no user in response, try to fetch user data
        await loadUserProfile();
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw new Error('Login failed. Please check your credentials and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    setIsLoading(true);
    try {
      await authService.logout(true);
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Signup function
  const signup = async (name: string, email: string, password: string): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await authService.signup({ name, email, password });
      
      // Check if response has user data directly or in data property
      const userData = response?.user || response.user;
      
      if (userData) {
        setUser(formatUserData(userData));
      } else {
        // If no user in response, try to fetch user data
        await loadUserProfile();
      }
    } catch (error) {
      console.error('Signup failed:', error);
      throw new Error('Signup failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Create value object
  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    signup,
    refreshUserData
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook for using auth context
export const useAuth = () => useContext(AuthContext); 